/**
 * Modal dialog: portalled, focus-trapped, Escape/backdrop to close, restores focus to
 * the previously focused element, makes the app root inert and pauses page hotkeys
 * (pushes the `"modal"` hotkey scope) while open.
 */
import clsx from 'clsx';
import { X } from 'lucide-react';
import { useEffect, useId, useRef, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useHotkeyScope } from '@/lib/hotkeys';
import { getFocusable, trapTab } from './focus';
import styles from './Modal.module.css';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  /** Action row (buttons), right-aligned. */
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Element to focus on open (default: first focusable in the body, else the dialog). */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /** Escape / backdrop / close button close the dialog (default `true`). */
  dismissible?: boolean;
  className?: string;
}

let openCount = 0;

function lockBackground(): () => void {
  openCount++;
  const root = document.getElementById('root');
  if (openCount === 1) {
    root?.setAttribute('inert', '');
    document.body.style.overflow = 'hidden';
  }
  return () => {
    openCount--;
    if (openCount === 0) {
      root?.removeAttribute('inert');
      document.body.style.overflow = '';
    }
  };
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  initialFocusRef,
  dismissible = true,
  className,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  useHotkeyScope('modal', open);

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const unlock = lockBackground();
    const target =
      initialFocusRef?.current ??
      (bodyRef.current ? getFocusable(bodyRef.current)[0] : undefined) ??
      dialogRef.current;
    target?.focus();
    return () => {
      unlock();
      if (previous?.isConnected) previous.focus();
    };
  }, [open, initialFocusRef]);

  if (!open) return null;

  return createPortal(
    <div
      className={styles.backdrop}
      onMouseDown={(e) => {
        if (dismissible && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className={clsx(styles.dialog, styles[size], className)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
            if (dismissible) onClose();
          }
          if (dialogRef.current) trapTab(e, dialogRef.current);
        }}
      >
        <header className={styles.header}>
          <div className={styles.titles}>
            <h2 id={titleId} className={styles.title}>
              {title}
            </h2>
            {description && (
              <p id={descriptionId} className={styles.description}>
                {description}
              </p>
            )}
          </div>
          {dismissible && (
            <button type="button" className={styles.close} aria-label="Close" onClick={onClose}>
              <X aria-hidden />
            </button>
          )}
        </header>
        {children !== undefined && (
          <div ref={bodyRef} className={styles.body}>
            {children}
          </div>
        )}
        {footer && <footer className={styles.footer}>{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}
